import React, { useState, useRef } from "react"
import { twMerge } from "tailwind-merge"

import ReviewStars from "../../components/shared/ReviewStars"

const VideoCard = ({ videoReview, className }) => {
  const [isMuted, setIsMuted] = useState(true)
  const videoRef = useRef(null)

  // Safety checks for data
  if (!videoReview || !videoReview.id) return null

  // Get client name and anonymization
  const clientName = videoReview.client?.firstName || "Anonymous"
  const clientLastName = videoReview.client?.lastName || ""
  const displayName = clientName !== "Anonymous" ? `${clientName} ${clientLastName[0] || ""}.` : "Anonymous"

  // Get rating from review if it exists
  const rating = videoReview.review?.rating || 5

  // Get date - use review date if available, otherwise video review date
  const reviewDate = videoReview.review?.createdAt || videoReview.createdAt
  const formattedDate = reviewDate
    ? new Date(reviewDate)
        .toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric"
        })
        .toUpperCase()
    : ""

  // Get booking count
  const bookingCount = videoReview.timesBooked || videoReview.times_booked || 0

  // Handle hover events for autoplay
  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.muted = true // Always ensure muted
      setIsMuted(true) // Update state to match
      videoRef.current.play()
    }
  }

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0 // Reset to beginning
    }
  }

  // Handle mute toggle
  const handleMuteToggle = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setIsMuted(videoRef.current.muted)
    }
  }

  return (
    <div
      className={twMerge("relative cursor-pointer overflow-hidden rounded-lg", className)}
      style={{ width: "14rem", height: "25rem" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {/* Booking Count Badge */}
      {bookingCount >= 0 && (
        <div className="absolute left-3 top-3 z-10 rounded bg-gray-800/75 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow">
          BOOKED {bookingCount} TIMES
        </div>
      )}
      {/* Video Player */}
      <video
        ref={videoRef}
        className="h-full w-full object-cover"
        src={videoReview.videoUrl || videoReview.video_url}
        poster={videoReview.thumbnailUrl}
        muted
        loop
        playsInline
        onLoadedMetadata={(e) => {
          // Set a default poster frame if no thumbnail
          if (!videoReview.thumbnailUrl) {
            e.target.currentTime = 1
          }
        }}
      />

      {/* Overlay Content */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

      {/* Bottom Content */}
      <div className="absolute inset-x-0 bottom-0 p-4 text-white">
        {/* Client Name */}
        <h3 className="mb-2 text-2xl font-bold text-white">{displayName}</h3>

        {/* Rating Stars */}
        <div className="mb-2">
          <ReviewStars rating={rating} starClassName="w-6 h-6" filledColor="#fbbf24" emptyColor="#6b7280" />
        </div>

        {/* Date */}
        <p className="text-xs font-medium uppercase tracking-wide">{formattedDate}</p>
      </div>

      {/* Mute/Unmute Button */}
      <button
        className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
        onClick={handleMuteToggle}>
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          {isMuted ? (
            <path
              fillRule="evenodd"
              d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.814L4.9 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.9l3.483-2.814a1 1 0 011.617.814zM12.146 5.146a1 1 0 011.414 0L15 6.586l1.44-1.44a1 1 0 111.414 1.414L16.414 8l1.44 1.44a1 1 0 01-1.414 1.414L15 9.414l-1.44 1.44a1 1 0 01-1.414-1.414L13.586 8l-1.44-1.44a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          ) : (
            <path
              fillRule="evenodd"
              d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.814L4.9 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.9l3.483-2.814a1 1 0 011.617.814zm7.133 2.09a1 1 0 011.414 0 6.018 6.018 0 010 8.51 1 1 0 11-1.414-1.414 4.018 4.018 0 000-5.682 1 1 0 010-1.414zm-2.37 2.37a1 1 0 011.414 0 2.018 2.018 0 010 2.828 1 1 0 11-1.414-1.414.018.018 0 000-.026 1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          )}
        </svg>
      </button>
    </div>
  )
}

const VideoReviewsSection = ({ videoReviews = [] }) => {
  // Filter out any null/undefined reviews and ensure we have valid data
  const validVideoReviews = videoReviews.filter((review) => review && review.id)

  // Early return if no video reviews
  if (!validVideoReviews || validVideoReviews.length === 0) return null

  return (
    <section className="mx-auto max-w-splash-container py-10 hplg:px-8 sm:px-6 sm:py-5">
      {/* <h2 className="mb-4 text-2xl font-bold">Video reviews</h2> */}
      <div className="grid grid-cols-3 gap-4 sm:grid-cols-1">
        {validVideoReviews.map((videoReview) => (
          <VideoCard key={videoReview.id} videoReview={videoReview} />
        ))}
      </div>
    </section>
  )
}

export default VideoReviewsSection
